import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "crm-admin" }
const _hoisted_2 = { class: "crm-shell" }
const _hoisted_3 = { class: "toolbar" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["disabled"]
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "filter" }
const _hoisted_8 = { class: "summary-grid" }
const _hoisted_9 = { class: "warn" }
const _hoisted_10 = { class: "danger" }
const _hoisted_11 = { class: "content-grid" }
const _hoisted_12 = { class: "table-area" }
const _hoisted_13 = ["onClick", "onDblclick"]
const _hoisted_14 = { class: "mono" }
const _hoisted_15 = { class: "side-panel" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { key: 0 }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { key: 0 }
const _hoisted_20 = {
  key: 0,
  class: "modal-backdrop"
}
const _hoisted_21 = { class: "modal" }
const _hoisted_22 = { class: "modal-tabs" }
const _hoisted_23 = { class: "crm-summary" }
const _hoisted_24 = {
  key: 0,
  class: "modal-body form-layout"
}
const _hoisted_25 = { class: "patient-picker" }
const _hoisted_26 = {
  key: 0,
  class: "patient-results"
}
const _hoisted_27 = ["onClick"]
const _hoisted_28 = { key: 0 }
const _hoisted_29 = { class: "wide" }
const _hoisted_30 = {
  key: 1,
  class: "modal-body action-layout"
}
const _hoisted_31 = {
  key: 2,
  class: "modal-body history-layout"
}
const _hoisted_32 = { key: 0 }

import { computed, reactive, ref } from 'vue';

type CrmType = '리콜' | '해피콜' | '예약미이행' | '컴플레인';
type CrmStatus = '대기' | '진행중' | '완료' | '보류' | '위험';
type Priority = '낮음' | '보통' | '높음';
type CallResult = '미연락' | '통화완료' | '부재중' | '문자발송' | '예약완료' | '내원완료';
type History = { id: number; time: string; action: string; memo: string };
type CrmRecord = {
  id: number;
  chartNo: string;
  patientName: string;
  phone: string;
  type: CrmType;
  status: CrmStatus;
  priority: Priority;
  dueDate: string;
  owner: string;
  lastVisit: string;
  nextReservation: string;
  content: string;
  callResult: CallResult;
  memo: string;
  nextAction: string;
  nextActionDate: string;
  history: History[];
};
type PatientOption = { id?: number; chartNo: string; name: string; phone: string; lastVisit?: string };
type Reservation = { id?: number; chartNo?: string; patient?: string; patientName?: string; phone?: string; date?: string; status?: string; treatment?: string };

const STORAGE_KEY = 'bioa-crm-records';


export default /*@__PURE__*/_defineComponent({
  __name: 'ClientManagePage',
  setup(__props) {

const records = ref<CrmRecord[]>(loadRecords());
const selectedRecordId = ref<number | null>(records.value[0]?.id ?? null);
const filterText = ref('');
const typeFilter = ref<'전체' | CrmType>('전체');
const statusFilter = ref<'전체' | CrmStatus>('전체');
const dateFilter = ref('');
const crmModalOpen = ref(false);
const isCreating = ref(false);
const modalTab = ref<'basic' | 'action' | 'history'>('basic');
const patientQuery = ref('');
const patientListOpen = ref(false);
const editingRecord = reactive<CrmRecord>(blankRecord());

const selectedRecord = computed(() => records.value.find((record) => record.id === selectedRecordId.value) || null);
const patientOptions = computed(() => loadPatientOptions());
const filteredPatients = computed(() => {
  const query = patientQuery.value.trim().toLowerCase();
  if (!query) return patientOptions.value.slice(0, 30);
  return patientOptions.value.filter((item) => [item.name, item.chartNo, item.phone].join(' ').toLowerCase().includes(query)).slice(0, 50);
});
const filteredRecords = computed(() => {
  const query = filterText.value.trim().toLowerCase();
  return records.value
    .filter((record) => typeFilter.value === '전체' || record.type === typeFilter.value)
    .filter((record) => statusFilter.value === '전체' || record.status === statusFilter.value)
    .filter((record) => !dateFilter.value || record.dueDate === dateFilter.value)
    .filter((record) => {
      if (!query) return true;
      return [record.patientName, record.chartNo, record.phone, record.type, record.content, record.memo, record.owner].join(' ').toLowerCase().includes(query);
    })
    .sort((a, b) => `${a.dueDate}-${a.id}`.localeCompare(`${b.dueDate}-${b.id}`));
});
const summary = computed(() => ({
  recall: records.value.filter((record) => record.type === '리콜' && record.status !== '완료').length,
  happy: records.value.filter((record) => record.type === '해피콜' && record.status !== '완료').length,
  missed: records.value.filter((record) => record.type === '예약미이행' && record.status !== '완료').length,
  complaint: records.value.filter((record) => record.type === '컴플레인' && record.status !== '완료').length,
}));
const todayTasks = computed(() => records.value.filter((record) => record.dueDate <= today() && record.status !== '완료').sort((a, b) => a.dueDate.localeCompare(b.dueDate)).slice(0, 8));
const riskTasks = computed(() => records.value.filter((record) => record.priority === '높음' || record.type === '컴플레인' || record.status === '위험').slice(0, 8));

function openCrmModal(record: CrmRecord | null) {
  isCreating.value = !record;
  Object.assign(editingRecord, record ? cloneRecord(record) : blankRecord());
  patientQuery.value = editingRecord.chartNo ? `${editingRecord.patientName} ${editingRecord.chartNo}` : '';
  patientListOpen.value = false;
  modalTab.value = 'basic';
  crmModalOpen.value = true;
}

function closeCrmModal() {
  crmModalOpen.value = false;
}

function saveCrmRecord() {
  if (!editingRecord.patientName.trim()) return;
  addHistory(editingRecord, '저장', `${editingRecord.type} ${editingRecord.status}`);
  const payload = cloneRecord(editingRecord);
  const index = records.value.findIndex((record) => record.id === payload.id);
  if (index >= 0) records.value[index] = payload;
  else records.value.unshift(payload);
  selectedRecordId.value = payload.id;
  persist();
  closeCrmModal();
}

function completeSelected() {
  if (!selectedRecord.value) return;
  selectedRecord.value.status = '완료';
  selectedRecord.value.callResult = selectedRecord.value.callResult === '미연락' ? '통화완료' : selectedRecord.value.callResult;
  addHistory(selectedRecord.value, '완료 처리', 'CRM 업무 완료');
  persist();
}

function scheduleRecall() {
  if (!selectedRecord.value) return;
  const source = selectedRecord.value;
  const next: CrmRecord = {
    ...cloneRecord(source),
    id: Date.now(),
    type: '리콜',
    status: '대기',
    priority: '보통',
    dueDate: addDays(30),
    content: `${source.patientName} 후속 리콜`,
    callResult: '미연락',
    nextAction: '후속 리콜',
    nextActionDate: addDays(30),
    history: [],
  };
  addHistory(next, '리콜 예약', `${source.content} 후속 관리`);
  records.value.unshift(next);
  selectedRecordId.value = next.id;
  persist();
}

function selectPatient(patient: PatientOption) {
  editingRecord.chartNo = patient.chartNo;
  editingRecord.patientName = patient.name;
  editingRecord.phone = patient.phone;
  editingRecord.lastVisit = patient.lastVisit || editingRecord.lastVisit;
  patientQuery.value = `${patient.name} ${patient.chartNo}`;
  patientListOpen.value = false;
}

function selectFirstPatient() {
  const first = filteredPatients.value[0];
  if (first) selectPatient(first);
}

function typeClass(type: CrmType) {
  return {
    recall: type === '리콜',
    happy: type === '해피콜',
    missed: type === '예약미이행',
    complaint: type === '컴플레인',
  };
}

function statusClass(status: CrmStatus) {
  return {
    waiting: status === '대기',
    working: status === '진행중',
    done: status === '완료',
    hold: status === '보류',
    risk: status === '위험',
  };
}

function addHistory(record: CrmRecord, action: string, memo: string) {
  record.history.unshift({ id: Date.now() + Math.random(), time: new Date().toISOString().slice(0, 16).replace('T', ' '), action, memo });
}

function blankRecord(): CrmRecord {
  return {
    id: Date.now(),
    chartNo: '',
    patientName: '',
    phone: '',
    type: '리콜',
    status: '대기',
    priority: '보통',
    dueDate: today(),
    owner: '상담실',
    lastVisit: '',
    nextReservation: '',
    content: '정기 리콜 안내',
    callResult: '미연락',
    memo: '',
    nextAction: '',
    nextActionDate: '',
    history: [],
  };
}

function cloneRecord(record: CrmRecord): CrmRecord {
  return JSON.parse(JSON.stringify(record));
}

function loadRecords(): CrmRecord[] {
  const stored = readStorage<CrmRecord[]>(STORAGE_KEY, []);
  if (stored.length) return stored.map(normalizeRecord);
  const seeded = seedRecords();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
  return seeded;
}

function normalizeRecord(record: Partial<CrmRecord>): CrmRecord {
  return {
    ...blankRecord(),
    ...record,
    history: record.history || [],
  };
}

function seedRecords(): CrmRecord[] {
  const missed = missedReservations().slice(0, 4).map((reservation, index) => normalizeRecord({
    id: 100 + index,
    chartNo: reservation.chartNo || `P-${1005 + index}`,
    patientName: reservation.patient || reservation.patientName || '예약환자',
    phone: reservation.phone || '',
    type: '예약미이행',
    status: '대기',
    priority: '높음',
    dueDate: today(),
    lastVisit: '',
    content: `${reservation.date || ''} ${reservation.treatment || '진료 예약'} 미이행 추적`,
    owner: '데스크',
  }));
  return missed;
}

function loadPatientOptions(): PatientOption[] {
  const patients = readStorage<Array<{ id?: number; chartNo?: string; name?: string; phone?: string }>>('bioa-patients', []);
  const visits = readStorage<Array<{ chartNo?: string; patientName?: string; phone?: string; date?: string }>>('bioa-visits', []);
  const merged = new Map<string, PatientOption>();
  patients.map((patient) => ({ id: patient.id, chartNo: patient.chartNo || '', name: patient.name || '', phone: patient.phone || '' }))
    .filter((patient) => patient.chartNo && patient.name)
    .forEach((patient) => merged.set(patient.chartNo, patient));
  visits.forEach((visit) => {
    if (!visit.chartNo) return;
    const current = merged.get(visit.chartNo);
    if (current) current.lastVisit = [current.lastVisit || '', visit.date || ''].sort().pop();
  });
  return Array.from(merged.values());
}

function missedReservations(): Reservation[] {
  return readStorage<Reservation[]>('bioa-reservations', []).filter((reservation) => ['미이행', '노쇼', '취소'].includes(reservation.status || ''));
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records.value));
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _cache[36] || (_cache[36] = _createElementVNode("nav", { class: "tabs" }, [
        _createElementVNode("button", { class: "active" }, "환자관리 CRM")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (openCrmModal(null)))
        }, "등록"),
        _createElementVNode("button", {
          disabled: !selectedRecord.value,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (selectedRecord.value && openCrmModal(selectedRecord.value)))
        }, "편집", 8, _hoisted_4),
        _createElementVNode("button", {
          disabled: !selectedRecord.value,
          onClick: completeSelected
        }, "완료 처리", 8, _hoisted_5),
        _createElementVNode("button", {
          disabled: !selectedRecord.value,
          onClick: scheduleRecall
        }, "리콜 예약", 8, _hoisted_6),
        _createElementVNode("label", _hoisted_7, [
          _cache[23] || (_cache[23] = _createElementVNode("span", null, "필터", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filterText).value = $event)),
            placeholder: "환자명/차트번호/전화번호/내용 검색"
          }, null, 512), [
            [_vModelText, filterText.value]
          ])
        ]),
        _createElementVNode("label", null, [
          _cache[25] || (_cache[25] = _createElementVNode("span", null, "업무", -1)),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((typeFilter).value = $event))
          }, _cache[24] || (_cache[24] = [
            _createElementVNode("option", null, "전체", -1),
            _createElementVNode("option", null, "리콜", -1),
            _createElementVNode("option", null, "해피콜", -1),
            _createElementVNode("option", null, "예약미이행", -1),
            _createElementVNode("option", null, "컴플레인", -1)
          ]), 512), [
            [_vModelSelect, typeFilter.value]
          ])
        ]),
        _createElementVNode("label", null, [
          _cache[27] || (_cache[27] = _createElementVNode("span", null, "상태", -1)),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((statusFilter).value = $event))
          }, _cache[26] || (_cache[26] = [
            _createElementVNode("option", null, "전체", -1),
            _createElementVNode("option", null, "대기", -1),
            _createElementVNode("option", null, "진행중", -1),
            _createElementVNode("option", null, "완료", -1),
            _createElementVNode("option", null, "보류", -1),
            _createElementVNode("option", null, "위험", -1)
          ]), 512), [
            [_vModelSelect, statusFilter.value]
          ])
        ]),
        _createElementVNode("label", null, [
          _cache[28] || (_cache[28] = _createElementVNode("span", null, "예정일", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((dateFilter).value = $event)),
            type: "date"
          }, null, 512), [
            [_vModelText, dateFilter.value]
          ])
        ])
      ]),
      _createElementVNode("section", _hoisted_8, [
        _createElementVNode("article", null, [
          _cache[29] || (_cache[29] = _createElementVNode("span", null, "리콜 대기", -1)),
          _createElementVNode("strong", null, _toDisplayString(summary.value.recall) + "건", 1)
        ]),
        _createElementVNode("article", null, [
          _cache[30] || (_cache[30] = _createElementVNode("span", null, "해피콜", -1)),
          _createElementVNode("strong", null, _toDisplayString(summary.value.happy) + "건", 1)
        ]),
        _createElementVNode("article", null, [
          _cache[31] || (_cache[31] = _createElementVNode("span", null, "예약 미이행", -1)),
          _createElementVNode("strong", _hoisted_9, _toDisplayString(summary.value.missed) + "건", 1)
        ]),
        _createElementVNode("article", null, [
          _cache[32] || (_cache[32] = _createElementVNode("span", null, "컴플레인", -1)),
          _createElementVNode("strong", _hoisted_10, _toDisplayString(summary.value.complaint) + "건", 1)
        ])
      ]),
      _createElementVNode("section", _hoisted_11, [
        _createElementVNode("section", _hoisted_12, [
          _createElementVNode("table", null, [
            _cache[33] || (_cache[33] = _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", null, "예정일"),
                _createElementVNode("th", null, "차트번호"),
                _createElementVNode("th", null, "환자"),
                _createElementVNode("th", null, "전화번호"),
                _createElementVNode("th", null, "업무"),
                _createElementVNode("th", null, "상태"),
                _createElementVNode("th", null, "중요도"),
                _createElementVNode("th", null, "담당자"),
                _createElementVNode("th", null, "최근 내원"),
                _createElementVNode("th", null, "내용")
              ])
            ], -1)),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredRecords.value, (record) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: record.id,
                  class: _normalizeClass({ selected: selectedRecordId.value === record.id, risk: record.priority === '높음' || record.type === '컴플레인' }),
                  onClick: ($event: any) => (selectedRecordId.value = record.id),
                  onDblclick: ($event: any) => (openCrmModal(record))
                }, [
                  _createElementVNode("td", null, _toDisplayString(record.dueDate), 1),
                  _createElementVNode("td", _hoisted_14, _toDisplayString(record.chartNo), 1),
                  _createElementVNode("td", null, _toDisplayString(record.patientName), 1),
                  _createElementVNode("td", null, _toDisplayString(record.phone || '-'), 1),
                  _createElementVNode("td", null, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["type", typeClass(record.type)])
                    }, _toDisplayString(record.type), 3)
                  ]),
                  _createElementVNode("td", null, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["status", statusClass(record.status)])
                    }, _toDisplayString(record.status), 3)
                  ]),
                  _createElementVNode("td", null, _toDisplayString(record.priority), 1),
                  _createElementVNode("td", null, _toDisplayString(record.owner), 1),
                  _createElementVNode("td", null, _toDisplayString(record.lastVisit || '-'), 1),
                  _createElementVNode("td", null, _toDisplayString(record.content), 1)
                ], 42, _hoisted_13))
              }), 128))
            ])
          ]),
          _createElementVNode("footer", null, _toDisplayString(filteredRecords.value.length) + "개 항목", 1)
        ]),
        _createElementVNode("aside", _hoisted_15, [
          _createElementVNode("section", null, [
            _cache[34] || (_cache[34] = _createElementVNode("header", null, "오늘 처리할 업무", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(todayTasks.value, (record) => {
              return (_openBlock(), _createElementBlock("button", {
                key: record.id,
                type: "button",
                onClick: ($event: any) => (openCrmModal(record))
              }, [
                _createElementVNode("strong", null, _toDisplayString(record.patientName), 1),
                _createElementVNode("span", null, _toDisplayString(record.type) + " · " + _toDisplayString(record.content), 1)
              ], 8, _hoisted_16))
            }), 128)),
            (!todayTasks.value.length)
              ? (_openBlock(), _createElementBlock("p", _hoisted_17, "오늘 예정된 CRM 업무가 없습니다."))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("section", null, [
            _cache[35] || (_cache[35] = _createElementVNode("header", null, "위험/컴플레인", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(riskTasks.value, (record) => {
              return (_openBlock(), _createElementBlock("button", {
                key: record.id,
                type: "button",
                onClick: ($event: any) => (openCrmModal(record))
              }, [
                _createElementVNode("strong", null, _toDisplayString(record.patientName), 1),
                _createElementVNode("span", null, _toDisplayString(record.status) + " · " + _toDisplayString(record.content), 1)
              ], 8, _hoisted_18))
            }), 128)),
            (!riskTasks.value.length)
              ? (_openBlock(), _createElementBlock("p", _hoisted_19, "위험 업무가 없습니다."))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ]),
    (crmModalOpen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
          _createElementVNode("section", _hoisted_21, [
            _createElementVNode("header", null, [
              _createElementVNode("strong", null, _toDisplayString(isCreating.value ? 'CRM 업무 등록' : `${editingRecord.patientName} CRM 상세`), 1),
              _createElementVNode("button", { onClick: closeCrmModal }, "×")
            ]),
            _createElementVNode("nav", _hoisted_22, [
              _createElementVNode("button", {
                class: _normalizeClass({ active: modalTab.value === 'basic' }),
                onClick: _cache[6] || (_cache[6] = ($event: any) => (modalTab.value = 'basic'))
              }, "기본 정보", 2),
              _createElementVNode("button", {
                class: _normalizeClass({ active: modalTab.value === 'action' }),
                onClick: _cache[7] || (_cache[7] = ($event: any) => (modalTab.value = 'action'))
              }, "처리 내용", 2),
              _createElementVNode("button", {
                class: _normalizeClass({ active: modalTab.value === 'history' }),
                onClick: _cache[8] || (_cache[8] = ($event: any) => (modalTab.value = 'history'))
              }, "이력", 2)
            ]),
            _createElementVNode("div", _hoisted_23, [
              _createElementVNode("div", null, [
                _cache[37] || (_cache[37] = _createElementVNode("span", null, "환자", -1)),
                _createElementVNode("strong", null, _toDisplayString(editingRecord.patientName || '환자 선택 필요'), 1)
              ]),
              _createElementVNode("div", null, [
                _cache[38] || (_cache[38] = _createElementVNode("span", null, "차트번호", -1)),
                _createElementVNode("strong", null, _toDisplayString(editingRecord.chartNo || '-'), 1)
              ]),
              _createElementVNode("div", null, [
                _cache[39] || (_cache[39] = _createElementVNode("span", null, "업무", -1)),
                _createElementVNode("strong", null, _toDisplayString(editingRecord.type), 1)
              ]),
              _createElementVNode("div", null, [
                _cache[40] || (_cache[40] = _createElementVNode("span", null, "상태", -1)),
                _createElementVNode("strong", null, _toDisplayString(editingRecord.status), 1)
              ])
            ]),
            (modalTab.value === 'basic')
              ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                  _createElementVNode("label", _hoisted_25, [
                    _cache[41] || (_cache[41] = _createElementVNode("span", { class: "field-label" }, [
                      _createTextVNode("환자 "),
                      _createElementVNode("em", null, "*")
                    ], -1)),
                    _createElementVNode("div", null, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((patientQuery).value = $event)),
                        placeholder: "이름/차트/전화 검색",
                        onFocus: _cache[10] || (_cache[10] = ($event: any) => (patientListOpen.value = true)),
                        onKeyup: _withKeys(selectFirstPatient, ["enter"])
                      }, null, 544), [
                        [_vModelText, patientQuery.value]
                      ]),
                      (patientListOpen.value && patientQuery.value.trim())
                        ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredPatients.value, (patient) => {
                              return (_openBlock(), _createElementBlock("button", {
                                key: patient.chartNo,
                                type: "button",
                                onClick: ($event: any) => (selectPatient(patient))
                              }, [
                                _createElementVNode("strong", null, _toDisplayString(patient.name), 1),
                                _createElementVNode("span", null, _toDisplayString(patient.chartNo) + " · " + _toDisplayString(patient.phone || '-'), 1)
                              ], 8, _hoisted_27))
                            }), 128)),
                            (!filteredPatients.value.length)
                              ? (_openBlock(), _createElementBlock("p", _hoisted_28, "검색 결과가 없습니다."))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _createElementVNode("label", null, [
                    _cache[43] || (_cache[43] = _createElementVNode("span", { class: "field-label" }, "업무 구분", -1)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((editingRecord.type) = $event))
                    }, _cache[42] || (_cache[42] = [
                      _createElementVNode("option", null, "리콜", -1),
                      _createElementVNode("option", null, "해피콜", -1),
                      _createElementVNode("option", null, "예약미이행", -1),
                      _createElementVNode("option", null, "컴플레인", -1)
                    ]), 512), [
                      [_vModelSelect, editingRecord.type]
                    ])
                  ]),
                  _createElementVNode("label", null, [
                    _cache[44] || (_cache[44] = _createElementVNode("span", { class: "field-label" }, "예정일", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((editingRecord.dueDate) = $event)),
                      type: "date"
                    }, null, 512), [
                      [_vModelText, editingRecord.dueDate]
                    ])
                  ]),
                  _createElementVNode("label", null, [
                    _cache[46] || (_cache[46] = _createElementVNode("span", { class: "field-label" }, "상태", -1)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((editingRecord.status) = $event))
                    }, _cache[45] || (_cache[45] = [
                      _createElementVNode("option", null, "대기", -1),
                      _createElementVNode("option", null, "진행중", -1),
                      _createElementVNode("option", null, "완료", -1),
                      _createElementVNode("option", null, "보류", -1),
                      _createElementVNode("option", null, "위험", -1)
                    ]), 512), [
                      [_vModelSelect, editingRecord.status]
                    ])
                  ]),
                  _createElementVNode("label", null, [
                    _cache[48] || (_cache[48] = _createElementVNode("span", { class: "field-label" }, "중요도", -1)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((editingRecord.priority) = $event))
                    }, _cache[47] || (_cache[47] = [
                      _createElementVNode("option", null, "낮음", -1),
                      _createElementVNode("option", null, "보통", -1),
                      _createElementVNode("option", null, "높음", -1)
                    ]), 512), [
                      [_vModelSelect, editingRecord.priority]
                    ])
                  ]),
                  _createElementVNode("label", null, [
                    _cache[49] || (_cache[49] = _createElementVNode("span", { class: "field-label" }, "담당자", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((editingRecord.owner) = $event))
                    }, null, 512), [
                      [_vModelText, editingRecord.owner]
                    ])
                  ]),
                  _createElementVNode("label", null, [
                    _cache[50] || (_cache[50] = _createElementVNode("span", { class: "field-label" }, "최근 내원일", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((editingRecord.lastVisit) = $event)),
                      type: "date"
                    }, null, 512), [
                      [_vModelText, editingRecord.lastVisit]
                    ])
                  ]),
                  _createElementVNode("label", null, [
                    _cache[51] || (_cache[51] = _createElementVNode("span", { class: "field-label" }, "다음 예약일", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((editingRecord.nextReservation) = $event)),
                      type: "date"
                    }, null, 512), [
                      [_vModelText, editingRecord.nextReservation]
                    ])
                  ]),
                  _createElementVNode("label", _hoisted_29, [
                    _cache[52] || (_cache[52] = _createElementVNode("span", { class: "field-label" }, "업무 내용", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((editingRecord.content) = $event)),
                      placeholder: "리콜 사유, 해피콜 목적, 컴플레인 요약"
                    }, null, 512), [
                      [_vModelText, editingRecord.content]
                    ])
                  ])
                ]))
              : _createCommentVNode("", true),
            (modalTab.value === 'action')
              ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                  _createElementVNode("section", null, [
                    _cache[56] || (_cache[56] = _createElementVNode("header", null, "처리 결과", -1)),
                    _createElementVNode("label", null, [
                      _cache[54] || (_cache[54] = _createElementVNode("span", { class: "field-label" }, "연락 결과", -1)),
                      _withDirectives(_createElementVNode("select", {
                        "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((editingRecord.callResult) = $event))
                      }, _cache[53] || (_cache[53] = [
                        _createElementVNode("option", null, "미연락", -1),
                        _createElementVNode("option", null, "통화완료", -1),
                        _createElementVNode("option", null, "부재중", -1),
                        _createElementVNode("option", null, "문자발송", -1),
                        _createElementVNode("option", null, "예약완료", -1),
                        _createElementVNode("option", null, "내원완료", -1)
                      ]), 512), [
                        [_vModelSelect, editingRecord.callResult]
                      ])
                    ]),
                    _createElementVNode("label", null, [
                      _cache[55] || (_cache[55] = _createElementVNode("span", { class: "field-label" }, "상담/처리 메모", -1)),
                      _withDirectives(_createElementVNode("textarea", {
                        "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((editingRecord.memo) = $event)),
                        rows: "7"
                      }, null, 512), [
                        [_vModelText, editingRecord.memo]
                      ])
                    ])
                  ]),
                  _createElementVNode("section", null, [
                    _cache[59] || (_cache[59] = _createElementVNode("header", null, "다음 조치", -1)),
                    _createElementVNode("label", null, [
                      _cache[57] || (_cache[57] = _createElementVNode("span", { class: "field-label" }, "다음 연락일", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((editingRecord.nextActionDate) = $event)),
                        type: "date"
                      }, null, 512), [
                        [_vModelText, editingRecord.nextActionDate]
                      ])
                    ]),
                    _createElementVNode("label", null, [
                      _cache[58] || (_cache[58] = _createElementVNode("span", { class: "field-label" }, "후속 조치", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((editingRecord.nextAction) = $event)),
                        placeholder: "예약 안내, 원장 상담 연결, 환불 검토 등"
                      }, null, 512), [
                        [_vModelText, editingRecord.nextAction]
                      ])
                    ])
                  ])
                ]))
              : _createCommentVNode("", true),
            (modalTab.value === 'history')
              ? (_openBlock(), _createElementBlock("div", _hoisted_31, [
                  _createElementVNode("ul", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(editingRecord.history, (history) => {
                      return (_openBlock(), _createElementBlock("li", {
                        key: history.id
                      }, [
                        _createElementVNode("span", null, _toDisplayString(history.time), 1),
                        _createElementVNode("strong", null, _toDisplayString(history.action), 1),
                        _createElementVNode("p", null, _toDisplayString(history.memo), 1)
                      ]))
                    }), 128))
                  ]),
                  (!editingRecord.history.length)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_32, "처리 이력이 없습니다."))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("footer", null, [
              _createElementVNode("button", {
                class: "ghost",
                onClick: closeCrmModal
              }, "취소"),
              _createElementVNode("button", {
                class: "primary",
                onClick: saveCrmRecord
              }, "저장")
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})